import React, { useState } from 'react';
import { Card, Tooltip, CardBody, Link } from '@nextui-org/react';
import Lottie from 'lottie-react';
import Youtube from '../lottie/youtube.json';
import { useTheme } from 'next-themes';
import { ReactComponent as Copy } from '../images/copy.svg'
import { ReactComponent as DarkCopy } from '../images/darkCopy.svg'
import VideoModal from './VideoModal';

interface LibraryCardProps {
    subTopicName: string;
    youtubeLink: string;
    themeNames?: string[];
}

const themeLabelClass = 'text-xs font-bold uppercase leading-tight tracking-wide text-darkBlue dark:text-lightBlue';

function ThemeNames({ names }: { names: string[] }) {
    const [expanded, setExpanded] = useState(false);

    if (names.length === 0) return null;

    if (names.length === 1) {
        return (
            <p className={`${themeLabelClass} mb-1.5 line-clamp-1`}>
                {names[0]}
            </p>
        );
    }

    const visible = expanded ? names : names.slice(0, 2);
    const hidden = expanded ? [] : names.slice(2);

    return (
        <div className='mb-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1'>
            {visible.map((name) => (
                <span
                    key={name}
                    title={name}
                    className={`${themeLabelClass} max-w-full whitespace-normal break-words rounded bg-darkBlue/10 px-1.5 py-0.5 dark:bg-lightBlue/15`}
                >
                    {name}
                </span>
            ))}
            {hidden.length > 0 && (
                <Tooltip
                    content={
                        <ul className='max-w-xs space-y-1 py-0.5 text-left'>
                            {hidden.map((name) => (
                                <li key={name} className='text-xs font-bold uppercase leading-tight'>{name}</li>
                            ))}
                        </ul>
                    }
                >
                    <button
                        type='button'
                        className='rounded px-1 py-0.5 text-xs font-bold leading-tight text-lightOrange'
                        aria-expanded={expanded}
                        aria-label={`${hidden.length} more ${hidden.length === 1 ? 'theme' : 'themes'}`}
                        onClick={() => setExpanded(true)}
                    >
                        +{hidden.length}
                    </button>
                </Tooltip>
            )}
        </div>
    );
}

const LibraryCard = ({ subTopicName, youtubeLink, themeNames = [] }: LibraryCardProps) => {
    const { theme } = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const showsThemes = themeNames.length > 0;

    const handleOpen = () => {
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
    };
    return (
        <Card className='w-full h-full border-2 border-lightOrange'>
            <CardBody className={'flex flex-row justify-between items-start gap-3 py-4'}>
                <div className='flex-1 min-w-0'>
                    <ThemeNames names={themeNames} />
                    <div className={`text-turquiose dark:text-lightGrey leading-snug ${showsThemes ? 'text-base sm:text-lg line-clamp-4 sm:line-clamp-5' : 'text-lg'}`}>
                        {subTopicName}
                    </div>
                </div>
                <div className='flex items-center gap-1 self-start pt-0.5'>
                    <Tooltip content='Play video'>
                        <div className={'mt-[1px] cursor-pointer ml-auto hidden md:block'} onClick={handleOpen}>
                            <Lottie
                                animationData={Youtube}
                                loop={true}
                                autoplay={true}
                                style={{
                                    width: 30,
                                    height: 30,
                                    margin: 0
                                }}
                            />
                        </div>

                    </Tooltip>
                    <Tooltip content='Open link in new tab'>
                        <Link isBlock showAnchorIcon href={`${youtubeLink}`} color='primary' isExternal className={'md:hidden mr-[5px]'} />
                    </Tooltip>
                    <Tooltip content='Copy link'>
                        <div className={'py-1 px-1 cursor-pointer mt-0.5'} onClick={() => { navigator.clipboard.writeText(youtubeLink) }}>
                            {theme === 'light' ? <Copy /> : <DarkCopy />}
                        </div>
                    </Tooltip>
                </div>
                <VideoModal
                    isOpen={isModalOpen}
                    onClose={handleClose}
                    youtubeLink={youtubeLink}
                    title={subTopicName}
                />
            </CardBody>
        </Card>
    );
};

export default LibraryCard;
